import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Card, CardContent, CardActions, Typography, Button, Box } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import LazyImage from './LazyImage';
import { trackProductClick } from '../analytics';

const ProductCard = React.memo(({ product, trackAffiliateClick, style }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const productCardStyles = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.shadows[10],
    },
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    width: '100%',
    minHeight: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '550px',
      margin: '0 auto'
    }
  };

  const cardRef = useRef(null);
  const handleCardClick = () => {
    trackProductClick(product);
    navigate(`/produkt/${product.asin || product.id}`);
  };

  // Anpassad för virtuell scrollning - ingen Grid container behövs längre
  return (
    <Box sx={{ padding: '8px', height: '100%', ...style }}>
      <Card 
        onClick={handleCardClick}
        sx={{ 
          ...productCardStyles, 
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        ref={cardRef}>
        <Box sx={{ 
          position: 'relative', 
          height: { xs: 180, sm: 220, md: 250 }, 
          width: '100%',
          overflow: 'hidden',
          flexShrink: 0
        }}>
          <Swiper
            spaceBetween={0}
            slidesPerView={1}
            pagination={{ clickable: true }}
            style={{ height: '100%' }}
          >
            {(product.images || (product.image ? [product.image] : ['/images/placeholder-image.svg'])).map((image, index) => (
              <SwiperSlide key={index}>
                <LazyImage
                  src={image}
                  alt={`${product.name} ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {product.discount > 0 && (
            <Box sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              bgcolor: 'error.main', 
              color: 'white', 
              px: 0.5, 
              py: 0.25,
              borderRadius: 1, 
              fontSize: '0.7rem',
              minWidth: '40px',
              textAlign: 'center',
              lineHeight: 1.2,
              boxShadow: theme.shadows[2],
              zIndex: 2
            }}>
              -{product.discount}%
            </Box>
          )}
        </Box>
        <Typography variant="h6" sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              textAlign: 'center',
              mt: 0.5,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}>
          {product.price} kr
        </Typography>
        {product.originalPrice && (
          <Typography variant="body2" style={{ textDecoration: 'line-through', textAlign: 'center', display: 'block' }}>
            {product.originalPrice} kr
          </Typography>
        )}
        <CardContent sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', p: 2, width: '100%' }}>
          <Typography variant={{ xs: 'body2', sm: 'h6' }} gutterBottom sx={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
            {product.name}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button
            variant="contained"
            color="primary"
            href={product.affiliateUrl || product.amazonUrl}
            target="_blank"
            onClick={e => {
              e.stopPropagation();
              trackAffiliateClick(product.name, product.asin);
            }}
            sx={{ width: { xs: '85%', sm: '90%' }, fontSize: { xs: '0.7rem', sm: '0.875rem' }, minHeight: { xs: 32, sm: 36 }, mt: 0 }}
          >
            Köp nu
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;